import { type Member } from '@/features/iam/members/Member'
import { type PayoutMethodStore } from '@/features/payouts/pro/PayoutMethodStore'
import { computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { type PayoutMethod } from '@/features/payouts/pro/PayoutMethod'
import type { RedirectToStripeConnectFn } from '@/features/billing'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * View model for a member's own payout method.
 */
export class ProfilePayoutMethodViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Payout method of the member.
   */
  @observable accessor payoutMethod: PayoutMethod | null = null

  constructor(
    readonly member: Member,
    private readonly _payoutMethodStore: PayoutMethodStore,
    private readonly _redirectToStripeConnect: RedirectToStripeConnectFn,
  ) {}

  /**
   * Whether the view is disabled.
   */
  @computed
  get disabled() {
    return this.fetch.pending || this.addPayoutMethod.pending
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The view renders a failed load from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Loads the member's payout method.
   */
  fetch = task(async (signal?: AbortSignal) => {
    const payoutMethod = await this._payoutMethodStore.fetchPayoutMethod(
      this.member.user.publicId,
      signal,
    )
    runInAction(() => {
      this.payoutMethod = payoutMethod
    })
  })

  /**
   * Adds a payout method.
   */
  addPayoutMethod = task.resolved(() => this._redirectToStripeConnect())

  /**
   * Connects the member's Stripe account with the code from the Stripe
   * Connect callback, then reloads the payout method.
   */
  async connectStripe(code: string) {
    await this._payoutMethodStore.connectStripe(this.member.user.publicId, code)
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }
}
